import React, { useRef, useEffect } from "react";
import { Button, ButtonDropdown, Spinner, StatusIndicator } from "@amzn/awsui-components-react/polaris";
import DatePicker from "@amzn/awsui-components-react/polaris/date-picker";
import {BACKSLASH, CREDENTIAL_FORMATS} from "../../../common/constants/constants";
import { fetchCountry, getDropdownItems } from "../utils/Utils";
import { uploadFiles } from "../../../common/utils/s3Utils";
import { uploadCredentials } from "../utils/credentialManagementAPIs";
import AppConfig from "../../../AppConfig";
import {v4 as uuid4} from "uuid";
import {getParentAndSubUseCase} from "../../excalibur/components/fetchAvailableUseCases";
import {FILE_SIZE_10_MB,excludedFiles} from "../utils/Constants";

const CREDENTIAL_TYPES = {
    CREDENTIAL: 'Credential',
    CERTIFICATE: 'Certificate',
};

Object.freeze(CREDENTIAL_TYPES);

function CredentialUploadControls({
    useCaseValue,
    selectedCredType,
    certificateExpiryDate,
    uploading,
    status,
    onUseCaseChange,
    onFileTypeChange,
    onExpiryDateChange,
    onDownloadTemplate,
    onUploadComplete,
    dispatch,
    domain
}) {
    const credentialsFileInputRef = useRef(null);
    const certificateFileInputRef = useRef(null);
    
    useEffect(() => {
        if (credentialsFileInputRef.current) {
            credentialsFileInputRef.current.value = '';
        }
        if (certificateFileInputRef.current) {
            certificateFileInputRef.current.value = '';
        }
    }, [selectedCredType]);

    const validateFiles = (files) => {
        const fileArray = Array.from(files);
        const errors = [];

        // Validate file sizes
        fileArray.forEach((file, index) => {
            // Check file size
            if (file.size > FILE_SIZE_10_MB) {
                errors.push(`File "${file.name}" exceeds 10MB size limit.`);
            }
        });

        return errors;
    };



    const callCredentialAuditAPI = async (uploadResults, domain, bucket) => {
        if (uploadResults.length === 0) {
            return;
        }
        try {
            const useCaseArray = getParentAndSubUseCase(useCaseValue);
            const useCase = useCaseArray[0];
            const subUseCase = useCaseArray[1];

            const expiryEpochTime = certificateExpiryDate ? new Date(certificateExpiryDate).getTime().toString() : null;

            const upload = uploadResults
            const filePath = upload.path;
            console.log(filePath)

            const auditPromise = new Promise((resolve) => {
                uploadCredentials(
                    (auditResponse) => {
                        if (auditResponse && auditResponse.message && !auditResponse.status) {
                            console.error(`Credential audit failed for upload batch:`, auditResponse.message);
                            resolve({
                                success: false,
                                message: auditResponse.message
                            });
                            return;
                        }
                        const isSuccessResponse = auditResponse && 
                            auditResponse.status === 'SUCCESS' &&
                            auditResponse.failedFilingIdentifierToErrorMessageMap &&
                            Object.keys(auditResponse.failedFilingIdentifierToErrorMessageMap).length === 0;

                        if (isSuccessResponse) {
                            console.log(`Credential audit successful`);
                            resolve({
                                success: true,
                                message: 'Upload successful'
                            });
                        } else {
                            let errorMessage = 'Upload failed';
                            
                            if (auditResponse) {
                                if (auditResponse.status === 'FAILED' || auditResponse.status === 'failed') {
                                    if (auditResponse.body.failedFilingIdentifierToErrorMessageMap) {
                                        const errorMessages = Object.values(auditResponse.failedFilingIdentifierToErrorMessageMap);
                                        errorMessage = errorMessages.length > 0 ? errorMessages.join(', ') : 'Upload failed';
                                    }
                                } else {
                                    errorMessage = `Unexpected response: ${JSON.stringify(auditResponse)}`;
                                }
                            }

                            console.error(`Credential audit failed for upload batch:`, errorMessage);
                            resolve({
                                success: false,
                                message: errorMessage
                            });
                        }
                    },
                    {
                        useCase,
                        subUseCase,
                        credentialType: selectedCredType,
                        s3Key: filePath,
                        s3Bucket: bucket,
                        expiryAt: expiryEpochTime,
                        setError: (error) => {
                            let errorMessage = 'Unexpected Error Occurred';
                            
                            if (error) {
                                if (error.message) {
                                    errorMessage = error.message;
                                } else if (typeof error === 'string') {
                                    errorMessage = error;
                                } else if (error.response && error.response.data && error.response.data.message) {
                                    errorMessage = error.response.data.message;
                                }
                            }

                            resolve({
                                success: false,
                                message: errorMessage
                            });
                        }
                    }
                ).catch((auditError) => {
                    console.error(`Audit API failed for upload batch:`, auditError);
                    resolve({
                        success: false,
                        message: auditError.message || 'Audit API failed'
                    });
                });
            });

            const auditResult = await auditPromise;

            if (auditResult.success) {
                dispatch({
                    type: 'SET_STATUS',
                    payload: {
                        isVisible: true,
                        type: 'success',
                        message: `Credential upload processed successfully (${upload.fileCount} file${upload.fileCount > 1 ? 's' : ''})`
                    }
                });
            } else {
                dispatch({
                    type: 'SET_STATUS',
                    payload: {
                        isVisible: true,
                        type: 'error',
                        message: `Upload failed: ${auditResult.message}`
                    }
                });
            }

            if (onUploadComplete) {
                onUploadComplete();
            }

        } catch (error) {
            console.error('Error in credential audit process:', error);
            dispatch({
                type: 'SET_STATUS',
                payload: {
                    isVisible: true,
                    type: 'error',
                    message: `Credential audit process failed: ${error.message || 'Unknown error'}`
                }
            });
        }
    };

    const handleFileChange = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            dispatch({ type: 'SET_STATUS', payload: { isVisible: true, type: 'error', message: 'No file or folder selected.' } });
            return;
        }

        const filteredFiles = Array.from(files).filter(
            file => !excludedFiles.some(excluded => file.name.includes(excluded))
        );

        const validationErrors = validateFiles(filteredFiles);
        if (validationErrors.length > 0) {
            dispatch({
                type: 'SET_STATUS',
                payload: {
                    isVisible: true,
                    type: 'error',
                    message: validationErrors.join(' ')
                }
            });
            return;
        }

        dispatch({ type: 'SET_UPLOADING', payload: true });
        console.log(`Uploading ${filteredFiles.length} file(s) for ${selectedCredType}.`);

        try {
            const bucket = AppConfig[domain].EXCALIBUR_BUCKET;
            const fileArray = Array.from(filteredFiles);



            const uuid = uuid4();

            let baseKey, displayName,filesToUpload;

            switch (selectedCredType) {
                case CREDENTIAL_TYPES.CERTIFICATE:
                    const firstFile = fileArray[0];
                    const relativePath = firstFile.webkitRelativePath;
                    const pathParts = relativePath.split(BACKSLASH);
                    const folderName = pathParts[0];
                    baseKey = `${useCaseValue}/${CREDENTIAL_TYPES.CERTIFICATE}/${uuid}`;
                    filesToUpload = fileArray.map(file => {
                        return new File([file], file.webkitRelativePath);
                    });
                    displayName = folderName;
                    break;

                case CREDENTIAL_TYPES.CREDENTIAL:
                    const fileName = fileArray[0].name;
                    displayName = fileName;
                    baseKey = `${useCaseValue}/${CREDENTIAL_TYPES.CREDENTIAL}/${uuid}/${fileName}`;
                    filesToUpload = fileArray;
                    break;

                default:
                    break;
            }


            // Upload files to S3
            try {
                await uploadFiles(filesToUpload, bucket, baseKey, domain);
                console.log(`Successfully uploaded ${fileArray.length} files for ${displayName} with UUID: ${uuid}`);
                
                // Create upload result for audit API
                const uploadResult = {
                    success: true,
                    displayName: displayName,
                    fileCount: fileArray.length,
                    uuid: uuid,
                    path: baseKey
                };

                dispatch({ type: 'SET_UPLOADING', payload: false });
                await callCredentialAuditAPI(uploadResult, domain, bucket);

            } catch (uploadError) {
                console.error(`Failed to upload ${displayName}:`, uploadError);
                dispatch({ type: 'SET_UPLOADING', payload: false });
                dispatch({
                    type: 'SET_STATUS',
                    payload: {
                        isVisible: true,
                        type: 'error',
                        message: `Upload failed: ${displayName} (${uploadError.message})`
                    }
                });
            }
        } catch (error) {
            console.error('Upload process failed:', error);
            dispatch({ type: 'SET_UPLOADING', payload: false });
            dispatch({
                type: 'SET_STATUS',
                payload: {
                    isVisible: true,
                    type: 'error',
                    message: `Upload failed: ${error.message || 'Unknown error occurred'}`
                }
            });
        }
    };

    const isTemplateDownloadEnabled = useCaseValue && selectedCredType;
    const isUploadSectionVisible = useCaseValue && selectedCredType;
    const isUploadButtonEnabled = isUploadSectionVisible;

    const handleUploadClick = () => {
        if (selectedCredType === CREDENTIAL_TYPES.CREDENTIAL) {
            credentialsFileInputRef.current?.click();
        } else if (selectedCredType === CREDENTIAL_TYPES.CERTIFICATE) {
            certificateFileInputRef.current?.click();
        }
    };

    return (
        <>
            {/* --- Selection Controls --- */}
            <div style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: '16px', 
                flexWrap: 'wrap', 
                alignItems: 'flex-start', 
                marginBottom: '16px',
                marginTop: '24px'
            }}>
                <ButtonDropdown
                    items={getDropdownItems()}
                    onItemClick={onUseCaseChange}
                >
                    {fetchCountry(useCaseValue)}
                </ButtonDropdown>

                <ButtonDropdown
                    items={[
                        { id: CREDENTIAL_TYPES.CREDENTIAL, text: 'Credential' }, 
                        { id: CREDENTIAL_TYPES.CERTIFICATE, text: 'Certificate' }
                    ]}
                    variant="normal"
                    disabled={!useCaseValue}
                    onItemClick={({ detail }) => onFileTypeChange(detail.id)}
                >
                    {selectedCredType ? 
                        `Type of Credential: ${selectedCredType.charAt(0).toUpperCase() + selectedCredType.slice(1)}` : 
                        'Type of file'
                    }
                </ButtonDropdown>

                <Button 
                    iconName="download" 
                    disabled={!isTemplateDownloadEnabled} 
                    onClick={onDownloadTemplate}
                >
                    Download Template
                </Button>
            </div>

            {/* --- Upload Controls (conditionally rendered) --- */}
            {isUploadSectionVisible && (
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    gap: '16px', 
                    flexWrap: 'wrap', 
                    alignItems: 'flex-start' 
                }}>
                    <div style={{ minWidth: '180px', maxWidth: '180px' }}>
                        <DatePicker
                            onChange={({ detail }) => onExpiryDateChange(detail.value)}
                            value={certificateExpiryDate}
                            placeholder="Expiry Date"
                            isDateEnabled={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return date >= today;
                            }}
                        />
                    </div>

                    <Button
                        iconName={selectedCredType === CREDENTIAL_TYPES.CERTIFICATE ? 'folder' : 'upload'}
                        variant="primary"
                        disabled={!isUploadButtonEnabled}
                        onClick={handleUploadClick}
                    >
                        {`Upload ${selectedCredType === CREDENTIAL_TYPES.CERTIFICATE ? 'Folder' : 'File'} of ${fetchCountry(useCaseValue)}`}
                    </Button>

                    {uploading && <Spinner />}
                    {status.isVisible && <StatusIndicator type={status.type}>{status.message}</StatusIndicator>}
                </div>
            )}

            {/* --- Hidden File Inputs --- */}
            <input
                ref={credentialsFileInputRef}
                type="file"
                hidden
                accept={CREDENTIAL_FORMATS}
                onChange={handleFileChange}
            />
            <input 
                ref={certificateFileInputRef} 
                type="file" 
                hidden 
                webkitdirectory="" 
                directory="" 
                multiple 
                onChange={handleFileChange}
            />
        </>
    );
}

export default CredentialUploadControls;
